import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Row, Col } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import { PolicyData, UserModel } from '../../auth/core/_models';
import { useAuth } from '../../auth';

type Props = {
  submissionError: string | null;
  creating: boolean;
  onSubmit: (values: ApolloFormValues) => Promise<void>;
  initialName?: string;
  initialEmail?: string;
  initialPhone?: string;
  CorporateCode?: string;
  currentUser?: UserModel;
  familyMembers?: Array<{ name: string; relation: string; gender: string; dob: string }>;
  loadingFamily?: boolean;
};

export interface ClinicData {
  CLINICTYPE: string;
  REGION: string;
  CITY: string;
  State: string;
  CLINICNAME: string;
  CLINICADDRESS: string;
  CLINICPIN: string;
  id: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApolloFormValues {
  Name: string;
  emailId: string;
  phoneNumber: string;
  bookingDate: string;
  bookingTime: string;
  state: string;
  city: string;
  clinicId: string;
  clinicName: string;
  clinicAddress: string;
  clinicPin: string;
  relation: string;
  gender: string;
  dob: string;
  appointmentFee: number;
  gst: number;
  totalFee: number;
}

const CLINICS_URL = 'https://etl.futurisk.in/webhook/6fc81a14-f7d4-4c38-a94b-8e470f6b8209';

const ApolloForm: React.FC<Props> = ({ submissionError, creating, onSubmit, initialName = '', initialEmail = '', initialPhone = '', CorporateCode = '', currentUser: propsCurrentUser, familyMembers: propsFamilyMembers = [], loadingFamily: propsLoadingFamily = false }) => {
  const auth = useAuth();
  const currentUser = propsCurrentUser || auth.currentUser;
  
  const [clinics, setClinics] = useState<ClinicData[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const familyMembers = propsFamilyMembers;
  const loadingFamily = propsLoadingFamily;

  // Generate time slots from 8 AM to 1 PM (one hour intervals)
  const timeSlots = [
    '08:00 AM - 09:00 AM',
    '09:00 AM - 10:00 AM',
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '12:00 PM - 01:00 PM',
  ];

  // Get minimum booking date respecting business rule (no same day, after 1 PM => day after tomorrow)
  const getMinBookingDate = () => {
    const now = new Date();
    const minDate = new Date(now);
    minDate.setDate(minDate.getDate() + 1);
    if (now.getHours() >= 13) {
      minDate.setDate(minDate.getDate() + 1);
    }
    const year = minDate.getFullYear();
    const month = String(minDate.getMonth() + 1).padStart(2, '0');
    const day = String(minDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const loadClinics = async () => {
      setLoadingClinics(true);
      try {
        const resp = await fetch(CLINICS_URL);
        if (resp.ok) {
          const data = await resp.json();
          if (Array.isArray(data)) {
            setClinics(data);
          }
        }
      } catch (err) {
        console.error('Unable to load clinics', err);
      } finally {
        setLoadingClinics(false);
      }
    };
    
    loadClinics();
  }, []);

  // Set gender and DOB when initialName is provided and family members are loaded
  useEffect(() => {
    if (initialName && familyMembers.length > 0 && !formik.values.gender && !formik.values.dob) {
      const member = familyMembers.find(m => m.name === initialName);
      if (member) {
        formik.setFieldValue('gender', member.gender);
        formik.setFieldValue('dob', member.dob);
        formik.setFieldValue('relation', member.relation);
        
        // Calculate and set payment values
        const fee = member.relation && !member.relation.toLowerCase().includes('self') ? 2500 : 0;
        const gstAmount = fee * 0.18;
        const total = fee + gstAmount;
        
        formik.setFieldValue('appointmentFee', fee);
        formik.setFieldValue('gst', gstAmount);
        formik.setFieldValue('totalFee', total);
      }
    }
  }, [initialName, familyMembers]);

  const states = useMemo(() => {
    const stateSet = new Set(clinics.map((clinic) => clinic.State));
    return Array.from(stateSet).sort();
  }, [clinics]);

  const apolloSchema = Yup.object().shape({
    Name: Yup.string().required('Name is required'),
    emailId: Yup.string().email('Invalid email').required('Email is required'),
    phoneNumber: Yup.string()
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
      .required('Phone number is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    clinicId: Yup.string().required('Please select a clinic'),
    bookingDate: Yup.string()
      .required('Booking date is required')
      .test('no-past-date', 'Cannot book past dates or same day', function (value) {
        if (!value) return false;
        const selectedDate = new Date(value);
        const minDate = new Date(getMinBookingDate());
        selectedDate.setHours(0, 0, 0, 0);
        minDate.setHours(0, 0, 0, 0);
        return selectedDate >= minDate;
      })
      .test('no-sunday', 'Bookings are not available on Sundays', function (value) {
        if (!value) return false;
        const selectedDate = new Date(value);
        return selectedDate.getDay() !== 0; // 0 is Sunday
      }),
    bookingTime: Yup.string().required('Time slot is required'),
  });

  const formik = useFormik({
    initialValues: {
      Name: initialName,
      emailId: initialEmail,
      phoneNumber: initialPhone,
      bookingDate: '',
      bookingTime: '',
      state: '',
      city: '',
      clinicId: '',
      clinicName: '',
      clinicAddress: '',
      clinicPin: '',
      relation: '',
      gender: '',
      dob: '',
      appointmentFee: 0,
      gst: 0,
      totalFee: 0,
    },
    validationSchema: apolloSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await onSubmit(values);
      setSubmitting(false);
    },
  });

  const cities = useMemo(() => {
    if (!formik.values.state) return [];
    const citySet = new Set(
      clinics.filter((clinic) => clinic.State === formik.values.state).map((clinic) => clinic.CITY)
    );
    return Array.from(citySet).sort();
  }, [clinics, formik.values.state]);

  const filteredClinics = useMemo(() => {
    if (!formik.values.state || !formik.values.city) return [];
    return clinics.filter(
      (clinic) => clinic.State === formik.values.state && clinic.CITY === formik.values.city
    );
  }, [clinics, formik.values.state, formik.values.city]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    formik.setFieldValue('state', value);
    formik.setFieldValue('city', '');
    formik.setFieldValue('clinicId', '');
    formik.setFieldValue('clinicName', '');
    formik.setFieldValue('clinicAddress', '');
    formik.setFieldValue('clinicPin', '');
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    formik.setFieldValue('city', value);
    formik.setFieldValue('clinicId', '');
    formik.setFieldValue('clinicName', '');
    formik.setFieldValue('clinicAddress', '');
    formik.setFieldValue('clinicPin', '');
  };

  const handleClinicSelect = (clinic: ClinicData) => {
    formik.setFieldValue('clinicId', String(clinic.id));
    formik.setFieldValue('clinicName', clinic.CLINICNAME);
    formik.setFieldValue('clinicAddress', clinic.CLINICADDRESS);
    formik.setFieldValue('clinicPin', clinic.CLINICPIN);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedName = e.target.value;
    const member = familyMembers.find(m => m.name === selectedName);
    formik.setFieldValue('Name', selectedName);
    formik.setFieldValue('relation', member?.relation || '');
    formik.setFieldValue('gender', member?.gender || '');
    formik.setFieldValue('dob', member?.dob || '');
    
    // Calculate and set payment values
    const fee = member?.relation && !member.relation.toLowerCase().includes('self') ? 2500 : 0;
    const gstAmount = fee * 0.18;
    const total = fee + gstAmount;
    
    formik.setFieldValue('appointmentFee', fee);
    formik.setFieldValue('gst', gstAmount);
    formik.setFieldValue('totalFee', total);
  };

  const appointmentFee = formik.values.appointmentFee;
  const gst = formik.values.gst;
  const totalFee = formik.values.totalFee;

  if (loadingClinics || loadingFamily) {
    return (
      <div className="text-center py-10">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3">{loadingClinics ? 'Loading clinic locations...' : 'Loading family members...'}</div>
      </div>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      {submissionError && (
        <div className="alert alert-danger" role="alert">{submissionError}</div>
      )}

      <Row className="mb-7">
        <Col md={12}>
          <label htmlFor="Name" className="form-label fw-bolder text-dark fs-6">Select Family Member</label>
          <select
            id="Name"
            name="Name"
            value={formik.values.Name}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.Name && formik.errors.Name,
              'is-valid': formik.touched.Name && !formik.errors.Name,
            })}
          >
            <option value="">Select Family Member</option>
            {familyMembers.map((member, index) => (
              <option key={index} value={member.name}>
                {member.name}
              </option>
            ))}
          </select>
          {formik.touched.Name && formik.errors.Name && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.Name}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Phone Number</label>
          <input
            type="text"
            maxLength={10}
            {...formik.getFieldProps('phoneNumber')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.phoneNumber && formik.errors.phoneNumber,
              'is-valid': formik.touched.phoneNumber && !formik.errors.phoneNumber,
            })}
            onInput={e => {
              const input = e.currentTarget;
              input.value = input.value.replace(/\D/g, '').slice(0, 10);
              formik.setFieldValue('phoneNumber', input.value);
            }}
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.phoneNumber}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Email</label>
          <input
            type="email"
            {...formik.getFieldProps('emailId')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.emailId && formik.errors.emailId,
              'is-valid': formik.touched.emailId && !formik.errors.emailId,
            })}
          />
          {formik.touched.emailId && formik.errors.emailId && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.emailId}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label htmlFor="state" className="form-label fw-bolder text-dark fs-6">State</label>
          <select
          id="state"
            name="state"
            value={formik.values.state}
            onChange={handleStateChange}
            onBlur={formik.handleBlur}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.state && formik.errors.state,
              'is-valid': formik.touched.state && !formik.errors.state,
            })}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {formik.touched.state && formik.errors.state && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.state}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label htmlFor="city" className="form-label fw-bolder text-dark fs-6">City</label>
          <select
            id="city"
            name="city"
            value={formik.values.city}
            onChange={handleCityChange}
            onBlur={formik.handleBlur}
            disabled={!formik.values.state}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.city && formik.errors.city,
              'is-valid': formik.touched.city && !formik.errors.city,
            })}
          >
            <option value="">Select City</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          {formik.touched.city && formik.errors.city && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.city}</span></div></div>
          )}
        </Col>
      </Row>

      {formik.values.city && (
        <Row className="mb-7">
          <Col md={12}>
            <label className="form-label fw-bolder text-dark fs-6">Select Clinic</label>
            {filteredClinics.length === 0 ? (
              <div className="alert alert-warning" role="alert">
                No clinics are available in the selected city.
              </div>
            ) : (
              <div className="clinic-list">
                {filteredClinics.map((clinic) => (
                  <div
                    key={clinic.id}
                    className={clsx('card mb-4', {
                      'border-primary border-2': formik.values.clinicId === String(clinic.id),
                      'border': formik.values.clinicId !== String(clinic.id),
                    })}
                  >
                    <div className="card-body py-3">
                      <div className="form-check d-flex align-items-start">
                        <input
                          className="form-check-input mt-2"
                          type="radio"
                          name="clinicId"
                          id={`clinic-${clinic.id}`}
                          checked={formik.values.clinicId === String(clinic.id)}
                          onChange={() => handleClinicSelect(clinic)}
                        />
                        <label className="form-check-label ms-3 w-100" htmlFor={`clinic-${clinic.id}`}>
                          <div className="fw-bold text-dark">{clinic.CLINICNAME}</div>
                          <div className="text-muted small mt-1">{clinic.CLINICADDRESS}</div>
                          <div className="text-muted small">PIN: {clinic.CLINICPIN}</div>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {formik.touched.clinicId && formik.errors.clinicId && (
              <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.clinicId}</span></div></div>
            )}
          </Col>
        </Row>
      )}

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Booking Date</label>
          <input
            type="date"
            min={getMinBookingDate()}
            {...formik.getFieldProps('bookingDate')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.bookingDate && formik.errors.bookingDate,
              'is-valid': formik.touched.bookingDate && !formik.errors.bookingDate,
            })}
          />
          {formik.touched.bookingDate && formik.errors.bookingDate && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.bookingDate}</span></div></div>
          )}
          <small className="text-muted d-block mt-2">
            {new Date().getHours() >= 13
              ? 'Slots booked after 1 PM start from the day after tomorrow.'
              : 'Same-day bookings are not available; earliest slot is tomorrow.'}
          </small>
        </Col>
        <Col md={6}>
          <label htmlFor="bookingTime" className="form-label fw-bolder text-dark fs-6">Time Slot</label>
          <select
            id="bookingTime"
            aria-label="Time Slot"
            title="Time Slot"
            {...formik.getFieldProps('bookingTime')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.bookingTime && formik.errors.bookingTime,
              'is-valid': formik.touched.bookingTime && !formik.errors.bookingTime,
            })}
          >
            <option value="">Select Time Slot</option>
            {timeSlots.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
          {formik.touched.bookingTime && formik.errors.bookingTime && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{formik.errors.bookingTime}</span></div></div>
          )}
        </Col>
      </Row>

      {formik.values.Name && formik.values.relation && (
        <div className="alert alert-info mb-7">
          <h5 className="fw-bold mb-3">Appointment Fee</h5>
          {formik.values.relation.toLowerCase().includes('self') ? (
            <div className="fs-4 fw-bold text-success">Free of Charge</div>
          ) : (
            <div>
              <div className="d-flex justify-content-between mb-2">
                <span>Consultation Fee:</span>
                <span className="fw-bold">₹{appointmentFee.toLocaleString('en-IN')}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>GST (18%):</span>
                <span className="fw-bold">₹{gst.toLocaleString('en-IN')}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <span className="fw-bold fs-5">Total Amount:</span>
                <span className="fw-bold fs-5 text-primary">₹{totalFee.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-center">
        <button type="submit" className="btn btn-lg btn-primary w-100 mb-5" disabled={!formik.isValid || formik.isSubmitting}>
          Book Appointment
        </button>
      </div>

      {creating && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center apollo-overlay">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Submitting...</div>
          </div>
        </div>
      )}
    </form>
  );
};

export { ApolloForm };
